// src/api/userMeApi.ts
//
// Bootstrap call that resolves the authenticated user against the backend
// access gate. The result drives the loading-screen handoff: provisioned
// users go to Welcome, unprovisioned users go to a sad-path screen.
//
// The backend never throws here — every observable outcome is encoded in
// the returned `AccessDecision`. Network failures collapse to `unknown` so
// the bootstrap can still proceed (fail-open) rather than wedging the
// loading spinner indefinitely on a transient backend hiccup.
//
// Deliberately NOT wired to the 401 refresh bridge (errorHandler.ts): this
// call runs right after a token is acquired, so a 401 here means that fresh
// token is unusable - triggering another refresh from inside the bootstrap
// would just loop against the same broken credential.

import { createLogger } from "../utils/logger";

const log = createLogger("UserMeApi");

// Read lazily inside `fetchUserMe` rather than capturing at module load.
// Webpack inlines `process.env.API_BASE_URL` at build time so this stays
// effectively constant in production, but at test time the env var is
// often set after the test file's imports have already run.
function getApiBaseUrl(): string | undefined {
  return process.env.API_BASE_URL;
}

/**
 * The five gate-deny statuses surfaced by the backend's access gate, plus
 * `unknown` for any non-403 failure (network error, unexpected status).
 *
 * Sourced from `api/services/access_gate_service.py:42-46`. Keep in sync if
 * the backend enum changes.
 */
export type AccessDenyStatus =
  | "no_org"
  | "no_user"
  | "suspended"
  | "inactive_user"
  | "identity_mismatch";

const DENY_STATUSES: ReadonlySet<string> = new Set([
  "no_org",
  "no_user",
  "suspended",
  "inactive_user",
  "identity_mismatch",
]);

export interface UserDto {
  id: string;
  name: string;
  email: string;
  active: boolean;
  organization_id: string | null;
  azure_oid: string | null;
  user_type: "individual" | "enterprise" | null;
}

export interface OrganizationDto {
  id: string;
  name: string;
  org_type: "enterprise" | "personal";
  deployment_type: "app_source" | "o365_tenant" | "dandilion_tenant";
  status: "active" | "suspended";
}

/**
 * Per-tenant feature entitlements the backend returns on `GET /users/me`,
 * derived from the tenant's operational config. The add-in gates feature UI on
 * these instead of global build-time flags, so a feature can be toggled per
 * customer without rebuilding the shared multi-tenant bundle.
 *
 * Mirrors the backend `FeatureEntitlements` schema. Fields are optional to keep
 * the frontend forward/backward compatible: a missing field means "the backend
 * reported nothing about this feature" and the consumer falls back to its
 * build-flag default. Add a field here when a global flag becomes a per-tenant
 * entitlement.
 */
export interface FeatureEntitlements {
  /** Document Library (SharePoint) — mirrors `document_library.enabled`. */
  document_library?: boolean;
  /** Conversation persistence across add-in restarts - mirrors `session_persistence.enabled`. */
  session_persistence?: boolean;
  /** Tier 3 review (Quick Analysis action) - mirrors `tier3_review.enabled`. */
  tier3_review?: boolean;
  /** Concise writing style toggle - mirrors `concise_mode.enabled`. */
  concise_mode?: boolean;
  /** Where the thinking toggle starts each app open - mirrors `extended_thinking.default_on`. */
  extended_thinking_default_on?: boolean;
}

export type AccessDecision =
  | {
      kind: "allowed";
      user: UserDto;
      organization: OrganizationDto | null;
      /**
       * Per-tenant entitlements. A field is absent when the backend reported
       * nothing about that feature (older backend, or it simply omitted it);
       * consumers treat an absent field as "no signal" and fall back to their
       * build-flag default, NOT as "disabled".
       */
      features?: FeatureEntitlements;
    }
  | { kind: "denied"; status: AccessDenyStatus; message: string }
  | {
      kind: "unknown";
      reason: "network" | "unauthenticated" | "unexpected";
      httpStatus?: number;
      message?: string;
    };

/**
 * Defensively parse the `features` object from a /users/me 200 body. Only
 * well-formed boolean fields are kept; anything missing or malformed is simply
 * omitted, so a consumer can distinguish "the backend said this feature is off"
 * (field present and false) from "the backend said nothing" (field absent → fall
 * back to the build flag). Returns undefined when there is no usable object.
 */
function parseFeatureEntitlements(raw: unknown): FeatureEntitlements | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const source = raw as Record<string, unknown>;
  const parsed: FeatureEntitlements = {};
  if (typeof source.document_library === "boolean") {
    parsed.document_library = source.document_library;
  }
  if (typeof source.session_persistence === "boolean") {
    parsed.session_persistence = source.session_persistence;
  }
  if (typeof source.tier3_review === "boolean") {
    parsed.tier3_review = source.tier3_review;
  }
  if (typeof source.concise_mode === "boolean") {
    parsed.concise_mode = source.concise_mode;
  }
  if (typeof source.extended_thinking_default_on === "boolean") {
    parsed.extended_thinking_default_on = source.extended_thinking_default_on;
  }
  return parsed;
}

/**
 * Calls GET /users/me with the bearer token. Never throws.
 *
 * The X-Subscription-Status header is the routing key on a 403; the
 * response body provides the user-facing message via the standard error
 * envelope (`{message, error_code, ...}`).
 */
export async function fetchUserMe(token: string): Promise<AccessDecision> {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    log.error("API_BASE_URL not configured");
    return { kind: "unknown", reason: "unexpected", message: "API not configured" };
  }

  const url = `${apiBaseUrl}/users/me`;

  // Bound the wait so a hung backend doesn't leave the loading spinner up
  // forever. The whole bootstrap is fail-open: a timeout collapses to the
  // same "unknown" decision as a connection refusal and the FE falls
  // through to the existing UI. Using AbortController + setTimeout rather
  // than AbortSignal.timeout() because browserslist still targets IE 11
  // (matches the pattern in draftingAssistantApi.ts / generalAgentApi.ts).
  const FETCH_TIMEOUT_MS = 10_000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  };
  // AWS WAF in front of the backend rejects requests without this header
  // with a 403 ("Access denied. Unauthorized API request."), short-circuiting
  // before the request reaches FastAPI's access gate. Every other call in the
  // codebase picks this up via constructRequest() in apiHandler.ts; /users/me
  // builds its own fetch so it has to attach the header itself.
  if (process.env.X_INTERNAL_TOKEN) {
    headers["X-Internal-Token"] = process.env.X_INTERNAL_TOKEN;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: "GET",
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    // AbortError (timeout) and network failures collapse to the same
    // outcome — both mean we couldn't reach a verdict from the backend.
    log.warn("Network error calling /users/me", { error: String(err) });
    return { kind: "unknown", reason: "network" };
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 200) {
    try {
      const body = (await response.json()) as {
        user?: UserDto;
        organization?: OrganizationDto | null;
        features?: unknown;
      };
      // Minimal runtime shape check — the type assertion lies on its own.
      // Catches null body, primitive body, missing `user` key, and any
      // schema drift that drops the user object entirely. Without this,
      // downstream consumers would access fields on `undefined` and crash
      // outside the bootstrap path's recovery envelope.
      if (!body || typeof body !== "object" || !body.user) {
        log.error("Malformed /users/me 200 body — missing user");
        return { kind: "unknown", reason: "unexpected", httpStatus: 200 };
      }
      return {
        kind: "allowed",
        user: body.user,
        organization: body.organization ?? null,
        features: parseFeatureEntitlements(body.features),
      };
    } catch (err) {
      log.error("Failed to parse /users/me 200 body", { error: String(err) });
      return { kind: "unknown", reason: "unexpected", httpStatus: 200 };
    }
  }

  if (response.status === 403) {
    const headerValue = response.headers.get("X-Subscription-Status");
    let message = "Access denied.";
    try {
      const body = await response.json();
      if (body && typeof body.message === "string") message = body.message;
    } catch {
      /* body not JSON; keep default message */
    }
    if (headerValue && DENY_STATUSES.has(headerValue)) {
      return {
        kind: "denied",
        status: headerValue as AccessDenyStatus,
        message,
      };
    }
    log.warn("403 from /users/me without recognised X-Subscription-Status", {
      header: headerValue,
    });
    return { kind: "unknown", reason: "unexpected", httpStatus: 403, message };
  }

  if (response.status === 401) {
    return {
      kind: "unknown",
      reason: "unauthenticated",
      httpStatus: 401,
    };
  }

  log.warn("Unexpected status from /users/me", { httpStatus: response.status });
  return { kind: "unknown", reason: "unexpected", httpStatus: response.status };
}
